import { escapeHtml } from './utils';

const ENDPOINT = '/api/method/zoni_edu.www.zoni.modules.blog.get_blog_cards';
const PLACEHOLDER_LOGO = '/assets/zoni_edu/assets/img/zoni_logo.svg';

let section;
let track;
let sentinel;
let readMoreLabel = '';
let pageSize = 6;
let total = 0;
let loaded = 0;
let fetching = false;
let exhausted = false;

/**
 * Builds one card. Mirrors templates/includes/home/blog_card.html —
 * keep the two in sync.
 */
function buildCard(post) {
    const image = post.meta_image
        ? `<img src="${encodeURI(post.meta_image)}" class="blog-card-img" loading="lazy"
                width="600" height="400" alt="${escapeHtml(post.title)}">`
        : `<div class="blog-card-img blog-card-img--placeholder" aria-hidden="true">
                <img src="${PLACEHOLDER_LOGO}" alt="" loading="lazy">
           </div>`;

    const category = post.category_title
        ? `<span class="blog-category">${escapeHtml(post.category_title)}</span>` : '';
    const date = post.published_label
        ? `<span class="blog-date">${escapeHtml(post.published_label)}</span>` : '';
    const sep = (post.category_title && post.published_label)
        ? ' <span class="blog-sep">&middot;</span> ' : '';

    return `
        <article class="blog-card" role="listitem">
            <a class="a-card-body-main" href="${escapeHtml(post.href)}">
                <div class="card sombra-card border-0 h-100">
                    ${image}
                    <div class="sect-card">
                        <div class="card-body">
                            <p class="blog-meta">${category}${sep}${date}</p>
                            <h4>${escapeHtml(post.title)}</h4>
                            <p class="blog-intro">${escapeHtml(post.blog_intro)}</p>
                            <span class="btn blog-readmore-btn">${escapeHtml(readMoreLabel)}</span>
                        </div>
                    </div>
                </div>
            </a>
        </article>`;
}

function appendPosts(posts) {
    if (!posts || !posts.length) {
        return;
    }
    const html = posts.map(buildCard).join('');
    sentinel.insertAdjacentHTML('beforebegin', html);
    loaded += posts.length;
    section.setAttribute('data-loaded', String(loaded));
}

function loadNextPage() {
    if (fetching || exhausted || loaded >= total) {
        return Promise.resolve();
    }
    fetching = true;

    const params = new URLSearchParams({ start: loaded, page_size: pageSize });
    return fetch(`${ENDPOINT}?${params.toString()}`, { method: 'GET' })
        .then(function (response) {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(function (data) {
            const payload = data.message || {};
            appendPosts(payload.posts);
            if (typeof payload.total_count === 'number') {
                total = payload.total_count;
            }
            if (!payload.has_more || !payload.posts || !payload.posts.length) {
                exhausted = true;
            }
            updateArrows();
        })
        .catch(function (error) {
            // Decorative section: never break the page, just stop paging.
            console.error('Error loading blog posts:', error);
            exhausted = true;
        })
        .finally(function () {
            fetching = false;
        });
}

function cardStep() {
    const card = track.querySelector('.blog-card');
    if (!card) {
        return track.clientWidth;
    }
    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return card.getBoundingClientRect().width + gap;
}

function updateArrows() {
    const left = document.getElementById('blog-arrow-left');
    const right = document.getElementById('blog-arrow-right');
    if (!left || !right) {
        return;
    }
    const maxScroll = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= maxScroll - 1 && exhausted;
    left.disabled = atStart;
    right.disabled = atEnd;
    left.classList.toggle('is-disabled', atStart);
    right.classList.toggle('is-disabled', atEnd);
}

function scrollByCards(direction) {
    track.scrollBy({ left: cardStep() * direction, behavior: 'smooth' });
}

function observeSentinel() {
    if (!('IntersectionObserver' in window)) {
        // No observer support: load everything up front rather than stranding
        // the user with six cards and no way to reach the rest.
        const loadAll = function () {
            if (exhausted || loaded >= total) return;
            loadNextPage().then(loadAll);
        };
        loadAll();
        return;
    }

    const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
            if (entry.isIntersecting) {
                loadNextPage();
            }
        });
    }, { root: track, rootMargin: '0px 400px 0px 0px' });

    observer.observe(sentinel);
}

export function initBlogSlider() {
    section = document.getElementById('blog-highlights');
    track = document.getElementById('blog-track');
    sentinel = document.getElementById('blog-sentinel');

    // The bundle runs on every page; bail out where the section is absent.
    if (!section || !track || !sentinel) {
        return;
    }

    total = parseInt(section.getAttribute('data-total'), 10) || 0;
    loaded = parseInt(section.getAttribute('data-loaded'), 10) || 0;
    pageSize = parseInt(section.getAttribute('data-page-size'), 10) || 6;
    readMoreLabel = section.getAttribute('data-label-readmore') || '';
    exhausted = loaded >= total;

    const left = document.getElementById('blog-arrow-left');
    const right = document.getElementById('blog-arrow-right');
    if (left) left.addEventListener('click', function () { scrollByCards(-1); });
    if (right) right.addEventListener('click', function () { scrollByCards(1); });

    track.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows, { passive: true });

    observeSentinel();
    updateArrows();
}
